import { Box, Card, CardContent, Switch, Typography } from "@mui/material";
import {
  NOTIFICATION_CHANNELS,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationPreference,
} from "@xop/dashboard-shared";
import { useEffect, useMemo, useState } from "react";

import { PageHeader } from "../../components/ui/page-header.js";
import { toast } from "../../components/ui/use-toast.js";
import { env } from "../../lib/env.js";
import {
  disableBrowserPush,
  enableBrowserPush,
  isPushSupported,
} from "../../lib/notifications/pushSubscribe.js";
import {
  useNotificationPrefs,
  useUpdateNotificationPrefs,
} from "../../lib/notifications/useNotifications.js";

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: "Email",
  desktop: "Desktop app",
  webpush: "Browser push",
};

const cellKey = (category: string, channel: NotificationChannel): string =>
  `${category}:${channel}`;

export default function NotificationsPage(): JSX.Element {
  const { data, isLoading, isError } = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();

  const [cells, setCells] = useState<Map<string, boolean>>(new Map());
  const [muted, setMuted] = useState(false);

  // Seed local state once the server payload arrives.
  useEffect(() => {
    if (!data) return;
    const next = new Map<string, boolean>();
    for (const p of data.preferences) next.set(cellKey(p.category, p.channel), p.enabled);
    setCells(next);
    setMuted(data.muted);
  }, [data]);

  const categories = data?.categories ?? [];
  const pushUnavailable = !env.vapidPublicKey || !isPushSupported();

  const persist = useMemo(
    () =>
      (nextCells: Map<string, boolean>, nextMuted: boolean): void => {
        const preferences: NotificationPreference[] = [];
        for (const cat of categories) {
          for (const channel of NOTIFICATION_CHANNELS) {
            preferences.push({
              category: cat.id,
              channel,
              enabled: nextCells.get(cellKey(cat.id, channel)) ?? false,
            });
          }
        }
        update.mutate(
          { preferences, muted: nextMuted },
          {
            onError: () =>
              toast({
                title: "Couldn't save",
                description: "Your notification preferences weren't saved.",
              }),
          },
        );
      },
    [categories, update],
  );

  const setCell = async (
    category: NotificationCategory,
    channel: NotificationChannel,
    value: boolean,
  ): Promise<void> => {
    // Turning browser push ON requires a live subscription first.
    if (channel === "webpush" && value) {
      const ok = await enableBrowserPush();
      if (!ok) {
        toast({
          title: "Browser push unavailable",
          description: pushUnavailable
            ? "Push isn't configured for this deployment."
            : "Permission was denied in the browser.",
        });
        return;
      }
    }
    if (channel === "webpush" && !value) {
      void disableBrowserPush();
    }
    setCells((prev) => {
      const next = new Map(prev);
      next.set(cellKey(category.id, channel), value);
      persist(next, muted);
      return next;
    });
  };

  const setRowMaster = (category: NotificationCategory, value: boolean): void => {
    setCells((prev) => {
      const next = new Map(prev);
      for (const channel of NOTIFICATION_CHANNELS) {
        // Browser push can only be turned on through the explicit toggle
        // (it needs a permission prompt), so the master leaves it off.
        next.set(cellKey(category.id, channel), value && channel !== "webpush");
      }
      persist(next, muted);
      return next;
    });
  };

  const onMuteToggle = (value: boolean): void => {
    setMuted(value);
    persist(cells, value);
  };

  if (isLoading) {
    return (
      <Box sx={{ mx: "auto", maxWidth: "48rem", p: 3 }}>
        <PageHeader title="Notifications" description="Loading…" />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box sx={{ mx: "auto", maxWidth: "48rem", p: 3 }}>
        <PageHeader title="Notifications" />
        <Typography sx={{ mt: 2, fontSize: 14, color: "error.main" }}>
          Could not load your notification preferences.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: "auto", maxWidth: "48rem", p: 3 }}>
      <PageHeader
        title="Notifications"
        description="Choose which internal events alert you, and how. You only see systems you have access to."
      />

      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
              Mute all notifications
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Pauses email, desktop and browser-push alerts. Your in-app
              inbox still fills.
            </Typography>
          </Box>
          <Switch
            checked={muted}
            onChange={(_e, checked) => onMuteToggle(checked)}
            inputProps={{ "aria-label": "Mute all notifications" }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {/* Header row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr repeat(3, 5.5rem)",
              alignItems: "center",
              gap: 1,
              borderBottom: 1,
              borderColor: "divider",
              px: 2,
              py: 1,
              fontSize: 12,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.025em",
              color: "text.disabled",
            }}
          >
            <Box component="span">System</Box>
            {NOTIFICATION_CHANNELS.map((ch) => (
              <Box component="span" key={ch} sx={{ textAlign: "center" }}>
                {CHANNEL_LABELS[ch]}
              </Box>
            ))}
          </Box>

          {categories.length === 0 ? (
            <Typography sx={{ px: 2, py: 3, fontSize: 14, color: "text.disabled" }}>
              You don't have access to any notifiable systems yet.
            </Typography>
          ) : (
            categories.map((cat) => (
              <Box
                key={cat.id}
                data-category={cat.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr repeat(3, 5.5rem)",
                  alignItems: "center",
                  gap: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  px: 2,
                  py: 1.5,
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      setRowMaster(
                        cat,
                        !NOTIFICATION_CHANNELS.some(
                          (ch) =>
                            ch !== "webpush" &&
                            cells.get(cellKey(cat.id, ch)),
                        ),
                      )
                    }
                    title="Toggle email + desktop for this system"
                    sx={{
                      display: "block",
                      width: "100%",
                      m: 0,
                      p: 0,
                      border: 0,
                      background: "none",
                      cursor: "pointer",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "text.primary",
                      borderRadius: 1,
                      "&:focus-visible": {
                        outline: (t) => `2px solid ${t.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {cat.label}
                  </Box>
                </Box>
                {NOTIFICATION_CHANNELS.map((ch) => {
                  const disabled =
                    muted || (ch === "webpush" && pushUnavailable);
                  return (
                    <Box key={ch} sx={{ display: "flex", justifyContent: "center" }}>
                      <Switch
                        checked={cells.get(cellKey(cat.id, ch)) ?? false}
                        disabled={disabled}
                        onChange={(_e, checked) => void setCell(cat, ch, checked)}
                        inputProps={{ "aria-label": `${cat.label} — ${CHANNEL_LABELS[ch]}` }}
                      />
                    </Box>
                  );
                })}
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {pushUnavailable && (
        <Typography sx={{ mt: 1, fontSize: 12, color: "text.disabled" }}>
          Browser push isn't available in this browser or isn't configured
          for this deployment.
        </Typography>
      )}
    </Box>
  );
}
